import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Download, Loader2, XCircle } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { useCart } from "@/context/CartContext";
import { api, API } from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const [downloads, setDownloads] = useState([]);
  const { clear } = useCart();

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return undefined;
    }
    let tries = 0;
    let timer;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data?.payment_status === "paid") {
          setState("paid");
          setDownloads(Array.isArray(data?.downloads) ? data.downloads : []);
          clear();
          return;
        }
        if (data?.status === "expired") return setState("error");
      } catch {
        if (!cancelled) return setState("error");
      }
      tries += 1;
      if (tries >= 10) setState("timeout");
      else timer = setTimeout(poll, 2000);
    };
    poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const copy = {
    checking: { icon: <Loader2 className="animate-spin text-[#c4dcca]" size={48} />, title: "Verifica pagamento…", text: "Stiamo confermando il tuo ordine con Stripe." },
    paid: { icon: <CheckCircle2 className="text-[#c4dcca]" size={48} />, title: "Ordine confermato.", text: "Grazie! I tuoi PDF sono pronti qui sotto. Salva questa pagina: i link restano validi." },
    timeout: { icon: <Loader2 className="text-[#a3b1aa]" size={48} />, title: "Pagamento in elaborazione", text: "Ci vuole più del previsto. Controlla la tua email tra qualche minuto." },
    error: { icon: <XCircle className="text-red-400" size={48} />, title: "Impossibile verificare", text: "Non siamo riusciti a confermare il pagamento. Contattaci se l'addebito è avvenuto." },
  }[state];

  return (
    <main data-testid="payment-success-page" className="grid min-h-screen place-items-center px-5">
      <div className="max-w-xl text-center">
        <LogoMark size={44} className="mx-auto mb-10" />
        <div className="mb-6 flex justify-center">{copy.icon}</div>
        <h1 data-testid="payment-status-title" className="font-display text-5xl font-900 uppercase md:text-6xl">{copy.title}</h1>
        <p className="mt-4 text-[#a3b1aa]">{state === "paid" && !downloads.length ? "Grazie! Il tuo ordine è stato registrato." : copy.text}</p>
        {downloads.length > 0 && (
          <div data-testid="downloads-list" className="mt-8 space-y-2 text-left">
            {downloads.map((d) => (
              <a key={d.product_id} data-testid={`download-${d.product_id}`} href={`${API}/downloads/${sessionId}/${d.product_id}`}
                className="flex items-center justify-between gap-3 border border-[rgba(152,191,162,0.3)] px-4 py-3 hover:border-[#c4dcca]">
                <span className="text-sm">{d.name}</span>
                <Download size={16} className="shrink-0 text-[#98bfa2]" />
              </a>
            ))}
          </div>
        )}
        <Link to="/" data-testid="back-home-btn" className="mt-10 inline-block bg-[#98bfa2] px-8 py-4 font-display text-lg font-800 uppercase text-[#050807] hover:bg-[#c4dcca]">
          Torna alla home
        </Link>
      </div>
    </main>
  );
}
